import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  AdminSupportingText,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import {
  DOCUMENT_MODULE_OPTIONS,
  buildSampleDocumentRules,
  labelFromOptions,
  formatUploadRulesSummary,
  formatCheckRulesSummary,
  formatExpiryRulesSummary,
  formatDocumentResultColumn,
  labelEntryPoint,
} from '../../../data/documentsConfig';

const displayStatusLabel = (status) => {
  const map = { draft: 'Draft', active: 'Active', inactive: 'Inactive' };
  return map[status] || (status && status.charAt(0).toUpperCase() + status.slice(1)) || '';
};

const Documents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [rules, setRules] = useState(buildSampleDocumentRules);

  const { rowActionsOpen, rowActionsRefs, tableScrollRef, rowActionsMenuLayout, rowActionsMenuRef, rowActionsMenuPlacementReady, toggleRowActions, closeRowActions } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    if (Array.isArray(location.state?.rules)) {
      setRules(location.state.rules);
    }
  }, [location.state?.rules]);

  const tableCols =
    'minmax(72px, 0.42fr) minmax(136px, 1fr) minmax(104px, 0.62fr) minmax(168px, 1fr) minmax(200px, 1.35fr) minmax(148px, 0.92fr) minmax(148px, 0.92fr) minmax(220px, 1.15fr) minmax(76px, 0.38fr) minmax(44px, 0.24fr)';

  const navigateWithRules = (path, partialState = {}) => navigate(path, { state: { rules, ...partialState } });

  const openRuleView = (rule) => {
    navigateWithRules(`/admin/configuration/documents/${urlNameSegment(rule.ruleName)}`, { rule: { ...rule } });
  };

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <AdminPageTitle className="!mb-0">Documents</AdminPageTitle>
          <AdminHeaderAction onClick={() => navigateWithRules('/admin/configuration/documents/add')}>Add Rule</AdminHeaderAction>
        </div>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="overflow-x-auto">
          <div
            className="grid py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2 min-w-[1520px]"
            style={{ gridTemplateColumns: tableCols }}
          >
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Rule ID</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Rule Name</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Module</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Entry Point</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Upload Rules</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Check Rules</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Expiry Rules</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0">
              <AdminTableHeader className="truncate">Document Result</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0 shrink-0">
              <AdminTableHeader>Status</AdminTableHeader>
            </div>
            <div className="flex items-center min-w-0 shrink-0">
              <AdminTableHeader>Actions</AdminTableHeader>
            </div>
          </div>

          {rules.length === 0 ? (
            <div className="py-10 px-4 text-center space-y-4">
              <AdminPanelTitle className="text-offblack">No document rules found</AdminPanelTitle>
              <AdminSupportingText className="text-mediumgray">
                Create a new document rule.
              </AdminSupportingText>
              <AdminHeaderAction onClick={() => navigateWithRules('/admin/configuration/documents/add')}>Add Rule</AdminHeaderAction>
            </div>
          ) : (
            paginatedRules.map((rule) => {
              const uploadCol = rule.uploadRule || formatUploadRulesSummary(rule);
              const checkCol = rule.checkRule || formatCheckRulesSummary(rule);
              const expiryCol = rule.expiryRule || formatExpiryRulesSummary(rule);
              const resultCol = rule.documentResult || formatDocumentResultColumn(rule);
              return (
                <div
                  key={rule.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openRuleView(rule)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openRuleView(rule);
                    }
                  }}
                  className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 min-w-[1520px] cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                  style={{ gridTemplateColumns: tableCols }}
                >
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={rule.id}>
                      {rule.id}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={rule.ruleName}>
                      {rule.ruleName}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block">{labelFromOptions(DOCUMENT_MODULE_OPTIONS, rule.ruleArea)}</AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={labelEntryPoint(rule.entryPoint)}>
                      {labelEntryPoint(rule.entryPoint) || '—'}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={uploadCol}>
                      {uploadCol}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={checkCol}>
                      {checkCol}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={expiryCol}>
                      {expiryCol}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 overflow-hidden">
                    <AdminTableBody className="truncate block" title={resultCol}>
                      {resultCol}
                    </AdminTableBody>
                  </div>
                  <div className="flex items-center min-w-0 shrink-0">
                    <AdminStatusBadge status={displayStatusLabel(rule.status)} label={displayStatusLabel(rule.status)} />
                  </div>
                  <div className="flex items-center justify-center shrink-0 overflow-visible" onClick={(e) => e.stopPropagation()}>
                    <div
                      className="relative inline-flex"
                      ref={(el) => {
                        rowActionsRefs.current[rule.id] = el;
                      }}
                    >
                      <AdminRowActionsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowActions(rule.id);
                        }}
                        aria-expanded={!!rowActionsOpen[rule.id]}
                        title="Actions"
                        aria-label="Row actions"
                      >
                        <AdminRowMenuIcon />
                      </AdminRowActionsButton>
                      {rowActionsOpen[rule.id] && rowActionsMenuLayout && (
                        <AdminFixedRowActionsMenu layout={rowActionsMenuLayout} menuRef={rowActionsMenuRef} placementReady={rowActionsMenuPlacementReady}>
                              <AdminRowMenuItem
                                onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                navigateWithRules(`/admin/configuration/documents/${urlNameSegment(rule.ruleName)}`, { rule: { ...rule } });
                              }}
                            >
                              View
                              </AdminRowMenuItem>
                              <AdminRowMenuItem
                                onClick={(e) => {
                                e.stopPropagation();
                                closeRowActions();
                                navigateWithRules(`/admin/configuration/documents/${urlNameSegment(rule.ruleName)}/edit`, { rule: { ...rule } });
                              }}
                            >
                              Edit
                              </AdminRowMenuItem>
                          </AdminFixedRowActionsMenu>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        </DashboardMainContainer>

        {rules.length > 0 && (
          <ConfigurationListPaginationFooter
            show={showPagination}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            goToPage={setCurrentPage}
          />
        )}

      </div>
    </AdminLayout>
  );
};

export default Documents;
